import { useEffect, useRef } from "react";
import { drawSparkline } from "../lib/charts";
import InfoButton, { type InfoContent } from "./InfoButton";
import AiBadge from "./AiBadge";

export type KpiBand = "critical" | "high" | "medium" | "info" | "good" | "low";
export type KpiDir = "good" | "warn" | "bad" | "neutral";

export interface KpiTileProps {
  id: string;             // KPI code, e.g. "K-001"
  band: KpiBand;          // colored rail + band chip
  label: string;
  sub?: string;
  value: string;
  delta?: number;
  deltaUnit?: string;
  deltaWindow?: string;
  direction?: KpiDir;
  interval?: string;
  spark?: number[];
  info?: InfoContent;
  /** Renders an "AI/ML" badge inline — for tiles whose value comes from a model. */
  ai?: boolean;
}

export default function KpiTile(k: KpiTileProps) {
  const sparkRef = useRef<SVGSVGElement>(null);
  useEffect(() => {
    if (sparkRef.current && k.spark && k.spark.length) {
      drawSparkline(sparkRef.current as unknown as SVGElement, k.spark, k.direction);
    }
  }, [k.spark, k.direction]);

  const dirCls = k.direction === "good" ? "good" : k.direction === "warn" ? "warn" : k.direction === "bad" ? "bad" : "neutral";
  const sign = k.delta == null ? "" : k.delta > 0 ? "▲ +" : k.delta < 0 ? "▼ " : "= ";

  return (
    <div className={`kpi-tile ${k.band}`}>
      <div className="rail" />
      <div className="kpi-head">
        <div className="kpi-id-row">
          <span className="kpi-id">{k.id}</span>
          <span className={`kpi-band ${k.band}`}>{k.band.toUpperCase()}</span>
          {k.ai && <AiBadge />}
        </div>
        {k.info && <InfoButton info={k.info} />}
      </div>
      <div className="kpi-label">{k.label}</div>
      {k.sub && <div className="kpi-sub">{k.sub}</div>}
      <div className="kpi-value-row">
        <div className="kpi-value">{k.value}</div>
        {k.delta != null && (
          <span className={`kpi-delta ${dirCls}`}>
            {sign}
            {Math.abs(k.delta)}
            {k.deltaUnit ?? ""}
            {k.deltaWindow && <span className="kpi-delta-window">{k.deltaWindow}</span>}
          </span>
        )}
      </div>
      {k.interval && <div className="kpi-interval">{k.interval}</div>}
      {k.spark && (
        <svg
          ref={sparkRef}
          className="kpi-spark"
          viewBox="0 0 200 36"
          preserveAspectRatio="none"
        />
      )}
    </div>
  );
}
